/**
 * Загрузка Yandex SmartCaptcha по требованию (T-17b).
 *
 * captcha.js не подключается в шаблоне: глобальный defer-скрипт держал
 * DOMContentLoaded и старт Vue. Формы вызывают loadSmartCaptcha(), когда капча
 * действительно нужна (открытие окна входа, форма регистрации).
 * Подключение — «расширенный метод» Яндекса: ?render=onload&onload=<callback>,
 * объект window.smartCaptcha гарантированно готов в callback.
 */

export const SMART_CAPTCHA_SITEKEY = 'ysc1_ZIZPbAUOGiHIF3riHEOjRAgdus42WtwZWiTBiZU7f38f6c3e';
export const SMART_CAPTCHA_LOAD_TIMEOUT_MS = 15000;
export const SMART_CAPTCHA_LOAD_ERROR_TEXT = 'Не удалось загрузить проверку «Я не робот». Обновите страницу или попробуйте позже';

const SCRIPT_URL = 'https://smartcaptcha.yandexcloud.net/captcha.js';
const ONLOAD_CALLBACK_NAME = 'specialtySmartCaptchaOnload';

const SCRIPT_STATE = {
    NONE: 'none',
    LOADING: 'loading',
};

let scriptState = SCRIPT_STATE.NONE;
let scriptElement = null;
let pending = null;

function getReadyCaptcha() {
    const captcha = window.smartCaptcha;
    return captcha && typeof captcha.render === 'function' ? captcha : null;
}

function settlePending(error) {
    if (!pending) return;

    const { resolve, reject, timer } = pending;
    pending = null;
    clearTimeout(timer);

    if (error) {
        reject(error);
        return;
    }

    const captcha = getReadyCaptcha();
    if (captcha) {
        resolve(captcha);
    } else {
        reject(new Error('smartCaptcha: объект не появился после onload'));
    }
}

function onScriptError() {
    if (scriptElement && scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
    }
    scriptElement = null;
    scriptState = SCRIPT_STATE.NONE;
    settlePending(new Error('smartCaptcha: ошибка загрузки captcha.js'));
}

function injectScript() {
    // callback остаётся навсегда: скрипт, догрузившийся после таймаута, не упадёт на вызове
    window[ONLOAD_CALLBACK_NAME] = () => settlePending(null);

    scriptElement = document.createElement('script');
    scriptElement.src = `${SCRIPT_URL}?render=onload&onload=${ONLOAD_CALLBACK_NAME}`;
    scriptElement.async = true;
    scriptElement.onerror = onScriptError;
    document.head.appendChild(scriptElement);
    scriptState = SCRIPT_STATE.LOADING;
}

/**
 * @return {Promise<Object>} window.smartCaptcha; reject — сеть, блокировщик или таймаут.
 * Повторный вызов после reject — новая попытка (скрипт, не ответивший к таймауту,
 * не дублируется: ждём его же).
 */
export function loadSmartCaptcha() {
    const readyCaptcha = getReadyCaptcha();
    if (readyCaptcha) return Promise.resolve(readyCaptcha);
    if (pending) return pending.promise;

    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
    });
    const timer = setTimeout(
        () => settlePending(new Error('smartCaptcha: таймаут загрузки')),
        SMART_CAPTCHA_LOAD_TIMEOUT_MS,
    );
    pending = {
        promise, resolve, reject, timer,
    };

    if (scriptState === SCRIPT_STATE.NONE) {
        injectScript();
    }

    return promise;
}
